import React, { useState } from 'react';
import emailjs from '@emailjs/browser';
import { Mail, Linkedin, MapPin, Send, Github, ArrowRight } from 'lucide-react';
import { consultantInfo } from '../mock';
import { useToast } from '../hooks/use-toast';

const contactLinks = [
  {
    icon: Mail,
    label: 'Email',
    value: 'sonalkarakshay.solapur@gmail.com',
    short: 'Drop a mail',
    href: `mailto:${consultantInfo.email}`,
    color: 'group-hover:text-emerald-400',
  },
  {
    icon: Linkedin,
    label: 'LinkedIn',
    value: 'linkedin.com/in/akshay-sonalkar',
    short: 'Connect professionally',
    href: `https://${consultantInfo.linkedin}`,
    color: 'group-hover:text-blue-400',
  },
  {
    icon: Github,
    label: 'GitHub',
    value: 'github.com/akshay-sonalkar',
    short: 'Explore my code',
    href: 'https://github.com/akshay-sonalkar',
    color: 'group-hover:text-violet-400',
  },
  {
    icon: MapPin,
    label: 'Location',
    value: 'Pune, Maharashtra, India',
    short: 'IST timezone',
    href: null,
    color: 'group-hover:text-rose-400',
  },
];

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        {
          from_name: formData.name,
          from_email: formData.email,
          message: formData.message,
        },
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY,
      );

      toast({ title: 'Message Sent!', description: "Thanks for reaching out. I'll get back to you soon." });
      setFormData({ name: '', email: '', message: '' });
    } catch {
      toast({ title: 'Submission Failed', description: 'Please try again or email me directly.', variant: 'destructive' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-28 px-4 sm:px-6 lg:px-8 relative overflow-hidden" style={{ background: 'linear-gradient(to bottom, #020617, #0a0f1e)' }}>
      {/* Background blobs */}
      <div className="absolute top-1/2 -right-48 w-[500px] h-[500px] bg-emerald-500/6 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-violet-500/5 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-6xl mx-auto">

        {/* Header */}
        <div className="text-center mb-16 reveal">
          <p className="text-xs font-mono text-emerald-400 uppercase tracking-[0.2em] mb-4">// contact</p>
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">
            Let's <span className="gradient-text">Connect.</span>
          </h2>
          <p className="text-slate-400 text-lg max-w-xl mx-auto">
            Whether it's about software, open source, mentorship, or just a friendly hello — I'd love to hear from you.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-10 items-start">

          {/* ── Left — Contact links ── */}
          <div className="lg:col-span-2 space-y-3 reveal reveal-delay-1">
            {contactLinks.map((item, index) => {
              const Icon = item.icon;
              const Inner = (
                <div className={`group flex items-start gap-4 p-4 rounded-xl bg-slate-900 border border-slate-800 hover:border-slate-600 transition-all duration-200 hover:-translate-y-0.5 ${item.href ? 'cursor-pointer' : ''}`}>
                  <div className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center shrink-0 group-hover:bg-slate-700 transition-colors">
                    <Icon className={`w-4 h-4 text-slate-400 transition-colors duration-200 ${item.color}`} />
                  </div>
                  <div className="min-w-0">
                    <div className="text-xs text-slate-500 mb-0.5 font-medium uppercase tracking-wider">{item.label}</div>
                    <div className="text-sm text-slate-300 font-medium truncate">{item.value}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{item.short}</div>
                  </div>
                  {item.href && <ArrowRight className="w-4 h-4 text-slate-600 group-hover:text-slate-400 group-hover:translate-x-1 transition-all duration-200 shrink-0 mt-3 ml-auto" />}
                </div>
              );

              return item.href ? (
                <a key={index} href={item.href} target="_blank" rel="noopener noreferrer">{Inner}</a>
              ) : (
                <div key={index}>{Inner}</div>
              );
            })}
          </div>

          {/* ── Right — Form ── */}
          <div className="lg:col-span-3 reveal reveal-delay-2">
            <div className="bg-slate-900 border border-slate-800 rounded-2xl p-8">
              <h3 className="text-xl font-bold text-white mb-6">Send a message</h3>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs text-slate-400 font-medium uppercase tracking-wider mb-2">Your Name *</label>
                    <input
                      name="name"
                      type="text"
                      placeholder="John Doe"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-slate-200 placeholder-slate-500 text-sm transition-all duration-200 input-glow focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-400 font-medium uppercase tracking-wider mb-2">Email Address *</label>
                    <input
                      name="email"
                      type="email"
                      placeholder="john@example.com"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-slate-200 placeholder-slate-500 text-sm transition-all duration-200 input-glow focus:outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs text-slate-400 font-medium uppercase tracking-wider mb-2">Message *</label>
                  <textarea
                    name="message"
                    placeholder="What's on your mind?"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-slate-200 placeholder-slate-500 text-sm resize-none transition-all duration-200 input-glow focus:outline-none"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full flex items-center justify-center gap-2.5 px-8 py-4 bg-emerald-500 hover:bg-emerald-400 disabled:bg-emerald-900 disabled:text-emerald-600 text-slate-950 font-bold rounded-xl transition-all duration-200 hover:scale-[1.02] glow-emerald-sm text-sm"
                >
                  {isSubmitting ? 'Sending...' : (
                    <>
                      Send Message
                      <Send className="w-4 h-4" />
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default Contact;
